interface ParsedTag {
  fullMatch: string;
  offset: number;
  numType: string;
  num: string;
  cleanNum: string;
  rsrcType: string;
  rsrcId: string;
}

/**
 * Where a sentence already leads into a figure: "... shown in FIG.", "... in FIGS.".
 * The models are told to write FIGS. before the tags, so the lead-in is usually there
 * already and a second one reads "FIGS. FIG. 3".
 */
const FIGURE_LEAD = /(?:fig|figs|figure|figures)\.?[*_\s]*$/i;

/**
 * A figure citation, written the way a drawing is cited: "FIG. 3", "FIGS. 3 and 4".
 *
 * Plain text, and deliberately not a link. A figure chip had nowhere to go — the document
 * viewer has a tab for the claims and one for the specification and nothing to scroll a
 * drawing to — and the same tag read as text came out as the document's own name,
 * "Publication 20250284582", which is the one thing the sentence did not mean. The sheet
 * itself is offered under the answer, where it opens.
 */
export const formatFigureCitation = (numbers: string[], precedingText = ""): string => {
  const figures = numbers.filter(Boolean);
  if (figures.length === 0) return "";

  const joined =
    figures.length === 1
      ? figures[0]
      : `${figures.slice(0, -1).join(", ")} and ${figures[figures.length - 1]}`;

  if (FIGURE_LEAD.test(precedingText)) return joined;
  return `${figures.length === 1 ? "FIG." : "FIGS."} ${joined}`;
};

/**
 * Parses <Rsrc> tags in message content and converts them to a format
 * suitable for markdown rendering. Groups consecutive tags from the same
 * resource together (e.g., "Application 18336766 Claims 1, 2, 3")
 */
export const replaceTagsWithNumbers = (html: string): string => {
  const regex = /<Rsrc([^>]*)\/>/g;
  const tags: ParsedTag[] = [];
  let match;

  // First pass: extract all tags
  while ((match = regex.exec(html)) !== null) {
    const fullMatch = match[0];
    const offset = match.index;
    const attributes = match[1];

    const numTypeMatch = attributes.match(/num_type="([^"]+)"/);
    const numMatch = attributes.match(/num="([^"]+)"/);
    const rsrcTypeMatch = attributes.match(/rsrc_type="([^"]+)"/);
    const rsrcIdMatch = attributes.match(/rsrc_id="([^"]+)"/);

    if (numTypeMatch && numMatch && rsrcTypeMatch && rsrcIdMatch) {
      const numType = numTypeMatch[1];
      const num = numMatch[1];
      const rsrcType = rsrcTypeMatch[1];
      const rsrcId = rsrcIdMatch[1];

      // For patent/publication paragraphs, preserve leading zeros for proper ID matching
      let cleanNum: string;
      if ((rsrcType === 'patent' || rsrcType === 'publication') && numType === 'paragraph') {
        cleanNum = num.replace(/^p-/, ''); // Remove 'p-' prefix but keep leading zeros
      } else {
        cleanNum = num.replace(/^p-0*/, '').replace(/^0+/, ''); // Original behavior for other types
      }

      tags.push({ fullMatch, offset, numType, num, cleanNum, rsrcType, rsrcId });
    }
  }

  // Second pass: group consecutive tags and build output
  const parts: string[] = [];
  let lastIndex = 0;
  let i = 0;

  while (i < tags.length) {
    const tag = tags[i];

    // Skip upload resource types entirely
    if (tag.rsrcType === 'upload') {
      const textBeforeMatch = html.substring(lastIndex, tag.offset);
      const trimmedText = textBeforeMatch.replace(/\s+$/, '');
      parts.push(trimmedText);
      lastIndex = tag.offset + tag.fullMatch.length;
      i++;
      continue;
    }

    // Skip undefined or empty values
    if (!tag.cleanNum || tag.cleanNum === 'undefined' || !tag.numType || tag.numType === 'undefined') {
      const textBeforeMatch = html.substring(lastIndex, tag.offset);
      const trimmedText = textBeforeMatch.replace(/\s+$/, '');
      parts.push(trimmedText);
      lastIndex = tag.offset + tag.fullMatch.length;
      i++;
      continue;
    }

    // Look ahead to find consecutive tags with same resource (allowing punctuation/conjunctions between)
    const group: ParsedTag[] = [tag];
    let j = i + 1;
    while (j < tags.length) {
      const nextTag = tags[j];

      // Check if next tag is from the same resource and num_type
      if (
        nextTag.rsrcType === tag.rsrcType &&
        nextTag.rsrcId === tag.rsrcId &&
        nextTag.numType === tag.numType &&
        nextTag.cleanNum !== tag.cleanNum // Don't include exact duplicates
      ) {
        // Check the text between current tag and next tag
        const prevTag = group[group.length - 1];
        const textBetween = html.substring(
          prevTag.offset + prevTag.fullMatch.length,
          nextTag.offset
        );

        // Only group if separated by whitespace, commas, and/or conjunctions (and, or, and/or)
        const separator = textBetween.trim();
        if (separator === '' || separator === ',' || separator === 'and' || separator === 'or' || separator === 'and/or' ||
            separator === ', and' || separator === ', or' || separator === ', and/or' ||
            separator === ',and' || separator === ',or' || separator === ',and/or') {
          group.push(nextTag);
          j++;
        } else {
          break;
        }
      } else {
        break;
      }
    }

    // Add text before the first tag in the group
    const textBefore = html.substring(lastIndex, tag.offset);
    parts.push(textBefore);

    // Render the group
    if (tag.numType === 'figure') {
      // A drawing is cited, not linked to - see formatFigureCitation
      parts.push(formatFigureCitation(group.map(t => t.cleanNum), textBefore));
    } else if (group.length === 1) {
      // Single tag - render as just the number
      parts.push(
        `<span class="rsrc-link" data-num-type="${tag.numType}" data-clean-num="${tag.cleanNum}" data-rsrc-type="${tag.rsrcType}" data-rsrc-id="${tag.rsrcId}">${tag.cleanNum}</span>`
      );
    } else {
      // Multiple consecutive tags - render as grouped format with parentheses and colon
      const resourceLabel = toTitleCase(tag.rsrcType);
      const numTypeLabel = tag.numType.charAt(0).toUpperCase() + tag.numType.slice(1) + 's';
      const numbers = group.map(t => t.cleanNum).join(', ');
      const displayText = `(${resourceLabel} ${tag.rsrcId}: ${numTypeLabel} ${numbers})`;

      // Create a clickable span that shows the full reference with all numbers
      parts.push(
        `<span class="rsrc-link" data-num-type="${tag.numType}" data-clean-num="${group[0].cleanNum}" data-rsrc-type="${tag.rsrcType}" data-rsrc-id="${tag.rsrcId}">${displayText}</span>`
      );
    }

    // Update lastIndex to after the last tag in the group
    const lastTagInGroup = group[group.length - 1];
    lastIndex = lastTagInGroup.offset + lastTagInGroup.fullMatch.length;
    i = j; // Skip all tags in the group
  }

  parts.push(html.substring(lastIndex));
  return parts.join('');
};

/**
 * Convert resource type to proper title case
 */
const toTitleCase = (str: string): string => {
  if (!str) return str;

  // Keep certain acronyms in all caps
  const upperCaseTypes = ['cfr', 'mpep', 'usc', 'epo', 'wipo', 'uspto'];
  const lowerStr = str.toLowerCase();

  if (upperCaseTypes.includes(lowerStr)) {
    return str.toUpperCase();
  }

  // Otherwise use title case (capitalize first letter)
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Format resource tag into display text
 */
export const formatResourceDisplay = (
  rsrcType: string,
  rsrcId: string,
  cleanNum: string,
  numType: string
): string => {
  if (rsrcType === 'cfr') {
    return `37 CFR ${rsrcId}`;
  } else if (rsrcType === 'mpep') {
    return `MPEP ${rsrcId}`;
  } else if (rsrcType === 'usc') {
    return `35 USC ${rsrcId}`;
  } else if (rsrcType === 'patent' && numType === 'paragraph') {
    return `Patent ${rsrcId} ¶${cleanNum}`;
  } else if (rsrcType === 'publication' && numType === 'paragraph') {
    return `Publication ${rsrcId} ¶${cleanNum}`;
  } else if (numType === 'figure') {
    return `FIG. ${cleanNum}`;
  } else {
    return `${toTitleCase(rsrcType)} ${rsrcId}`;
  }
};

/**
 * Strips <Rsrc> tags from content and replaces them with full formatted resource display
 * Used when copying text to clipboard. Groups consecutive tags from the same resource.
 */
export const stripResourceTags = (html: string): string => {
  const regex = /<Rsrc([^>]*)\/>/g;
  const tags: ParsedTag[] = [];
  let match;

  // First pass: extract all tags
  while ((match = regex.exec(html)) !== null) {
    const fullMatch = match[0];
    const offset = match.index;
    const attributes = match[1];

    const numTypeMatch = attributes.match(/num_type="([^"]+)"/);
    const numMatch = attributes.match(/num="([^"]+)"/);
    const rsrcTypeMatch = attributes.match(/rsrc_type="([^"]+)"/);
    const rsrcIdMatch = attributes.match(/rsrc_id="([^"]+)"/);

    if (numTypeMatch && numMatch && rsrcTypeMatch && rsrcIdMatch) {
      const numType = numTypeMatch[1];
      const num = numMatch[1];
      const rsrcType = rsrcTypeMatch[1];
      const rsrcId = rsrcIdMatch[1];

      // For patent/publication paragraphs, preserve leading zeros
      let cleanNum: string;
      if ((rsrcType === 'patent' || rsrcType === 'publication') && numType === 'paragraph') {
        cleanNum = num.replace(/^p-/, '');
      } else {
        cleanNum = num.replace(/^p-0*/, '').replace(/^0+/, '');
      }

      tags.push({ fullMatch, offset, numType, num, cleanNum, rsrcType, rsrcId });
    }
  }

  // Second pass: group consecutive tags and build output
  const parts: string[] = [];
  let lastIndex = 0;
  let i = 0;

  while (i < tags.length) {
    const tag = tags[i];

    // Skip upload resource types entirely
    if (tag.rsrcType === 'upload') {
      const textBeforeMatch = html.substring(lastIndex, tag.offset);
      const trimmedText = textBeforeMatch.replace(/\s+$/, '');
      parts.push(trimmedText);
      lastIndex = tag.offset + tag.fullMatch.length;
      i++;
      continue;
    }

    // Skip undefined or empty values
    if (!tag.cleanNum || tag.cleanNum === 'undefined' || !tag.numType || tag.numType === 'undefined') {
      const textBeforeMatch = html.substring(lastIndex, tag.offset);
      const trimmedText = textBeforeMatch.replace(/\s+$/, '');
      parts.push(trimmedText);
      lastIndex = tag.offset + tag.fullMatch.length;
      i++;
      continue;
    }

    // Look ahead to find consecutive tags with same resource (allowing punctuation/conjunctions between)
    const group: ParsedTag[] = [tag];
    let j = i + 1;
    while (j < tags.length) {
      const nextTag = tags[j];

      // Check if next tag is from the same resource and num_type
      if (
        nextTag.rsrcType === tag.rsrcType &&
        nextTag.rsrcId === tag.rsrcId &&
        nextTag.numType === tag.numType &&
        nextTag.cleanNum !== tag.cleanNum // Don't include exact duplicates
      ) {
        // Check the text between current tag and next tag
        const prevTag = group[group.length - 1];
        const textBetween = html.substring(
          prevTag.offset + prevTag.fullMatch.length,
          nextTag.offset
        );

        // Only group if separated by whitespace, commas, and/or conjunctions (and, or, and/or)
        const separator = textBetween.trim();
        if (separator === '' || separator === ',' || separator === 'and' || separator === 'or' || separator === 'and/or' ||
            separator === ', and' || separator === ', or' || separator === ', and/or' ||
            separator === ',and' || separator === ',or' || separator === ',and/or') {
          group.push(nextTag);
          j++;
        } else {
          break;
        }
      } else {
        break;
      }
    }

    // Add text before the first tag in the group
    const textBefore = html.substring(lastIndex, tag.offset);
    parts.push(textBefore);

    // Render the group
    if (tag.numType === 'figure') {
      parts.push(formatFigureCitation(group.map(t => t.cleanNum), textBefore));
    } else if (group.length === 1) {
      // Single tag - render with full format
      const displayText = formatResourceDisplay(tag.rsrcType, tag.rsrcId, tag.cleanNum, tag.numType);
      parts.push(displayText);
    } else {
      // Multiple consecutive tags - render as grouped format with parentheses and colon
      const resourceLabel = toTitleCase(tag.rsrcType);
      const numTypeLabel = tag.numType.charAt(0).toUpperCase() + tag.numType.slice(1) + 's';
      const numbers = group.map(t => t.cleanNum).join(', ');
      parts.push(`(${resourceLabel} ${tag.rsrcId}: ${numTypeLabel} ${numbers})`);
    }

    // Update lastIndex to after the last tag in the group
    const lastTagInGroup = group[group.length - 1];
    lastIndex = lastTagInGroup.offset + lastTagInGroup.fullMatch.length;
    i = j; // Skip all tags in the group
  }

  parts.push(html.substring(lastIndex));
  return parts.join('');
};
